import React, { useMemo, useState } from "react";
import privateAxios from "../../utils/axiosPrivate";
import TablePagesTitle from "../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../accessmanagment/page-action/AccessPageAction";
import { showToast } from "../../utils/utils";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  createPricingPlan,
  commonErrMsg,
  createPricingPlanGrp,
  buttonName,
  alertActions,
  fieldValidation,
  myAccount,
  createAccPage,
  commonText
} from "../../locales/en";
import { NA, showToastType, ZERO } from "../../utils/constants/appConstants";
import { useSelector } from "react-redux";
import { getCreateCompletePricingPlanURL } from "../../utils/constants/apiEndPoints";
import { sanitizeInput } from "../../utils/utils";
import {
  getAccountingSoftwareOptions,
  getCountryOptions,
  getOrgTypeOptions,
  getOwnPropertyOptions,
  getSingleUseOptions
} from "../../utils/getDropdownOptions";
import {
  isApplicableField,
  isPercentageField
} from "../../utils/constants/formDataFields";
import { useQuoteStatus } from "../../hooks/useQuoteStatus";
import { PRICING_PLAN_WITH_PLAN_TYPE } from "../../route/paths";
import IndustryTypeSelect from "./IndustryTypeSelect";

export default function CreatePricingPlanSection() {
  const { id } = useParams();
  const { planGroup } = useSelector((state) => state.pricingPlan);
  const userStatus = useQuoteStatus();

  const initialPricingPlan = {
    pricingPlanName: "",
    flatRate: "",
    annualRate: "",
    extraFlatRate: "",
    extraAnnualRate: "",
    isAutoSwitching: true,
    triggerEvents: [],
    industryCategoryId: null,
    invoiceSyncType: NA,
    eligibleOrganizationType: NA,
    applicableCountry: NA,
    ownershipStatus: NA,
    isRiskBased: true,
    minApplicableValue: "",
    maxApplicableValue: "",
    primaryPricingPlanId: id
  };
  const [pricingPlan, setPricingPlan] = useState(initialPricingPlan);
  const [errors, setErrors] = useState({});
  const history = useNavigate();

  const {
    pricingPlanName,
    flatRate,
    annualRate,
    extraFlatRate,
    extraAnnualRate,
    triggerEvents,
    industryCategoryId,
    invoiceSyncType,
    eligibleOrganizationType,
    applicableCountry,
    ownershipStatus,
    isRiskBased,
    minApplicableValue,
    maxApplicableValue,
    primaryPricingPlanId
  } = pricingPlan;

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (type === "checkbox") {
      newValue = checked;
    } else {
      newValue = sanitizeInput(value).trim();
    }

    if (name === "isRiskBased") {
      newValue = value === createPricingPlan?.propertyOptions?.item1;
    }

    if (name === "isAutoSwitching") {
      newValue = value === createPricingPlan?.active;
    }

    if (isPercentageField(name)) {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        newValue = parsedValue > 100 ? 100 : parsedValue;
      } else {
        newValue = "";
      }
    }

    if (isApplicableField(name)) {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        newValue = parsedValue > 1 ? 1 : parsedValue;
      } else {
        newValue = "";
      }
    }

    setPricingPlan((prevState) => {
      const updatedPlan = { ...prevState, [name]: newValue };
      const tempErrors = { ...errors };

      if (
        updatedPlan.minApplicableValue !== "" &&
        updatedPlan.maxApplicableValue !== "" &&
        parseFloat(updatedPlan.minApplicableValue) >
          parseFloat(updatedPlan.maxApplicableValue)
      ) {
        tempErrors.minApplicableValue = createPricingPlan?.errMsg.item1;
        tempErrors.maxApplicableValue = createPricingPlan?.errMsg.item2;
      } else {
        delete tempErrors.minApplicableValue;
        delete tempErrors.maxApplicableValue;
      }

      setErrors(tempErrors);
      return updatedPlan;
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ""
    }));
  };

  const validate = () => {
    const tempErrors = {};

    if (!pricingPlanName.trim()) {
      tempErrors.pricingPlanName = fieldValidation?.required;
    }

    if (flatRate === null || flatRate === undefined || flatRate === "") {
      tempErrors.flatRate = fieldValidation?.required;
    }

    if (annualRate === null || annualRate === undefined || annualRate === "") {
      tempErrors.annualRate = fieldValidation?.required;
    }

    if (
      extraFlatRate === null ||
      extraFlatRate === undefined ||
      extraFlatRate === ""
    ) {
      tempErrors.extraFlatRate = fieldValidation?.required;
    }

    if (
      extraAnnualRate === null ||
      extraAnnualRate === undefined ||
      extraAnnualRate === ""
    ) {
      tempErrors.extraAnnualRate = fieldValidation?.required;
    }

    if (!triggerEvents || triggerEvents.length === 0) {
      tempErrors.triggerEvents = fieldValidation?.required;
    }

    if (pricingPlan.isRiskBased === true && !primaryPricingPlanId) {
      tempErrors.primaryPricingPlanId = fieldValidation?.required;
    }

    if (
      pricingPlan.isRiskBased === true &&
      (minApplicableValue === null ||
        minApplicableValue === undefined ||
        minApplicableValue === "")
    ) {
      tempErrors.minApplicableValue = fieldValidation?.required;
    }

    if (
      pricingPlan.isRiskBased === true &&
      (maxApplicableValue === null ||
        maxApplicableValue === undefined ||
        maxApplicableValue === "")
    ) {
      tempErrors.maxApplicableValue = fieldValidation?.required;
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    try {
      const isRiskBased = pricingPlan.isRiskBased;
      const additionalValues = isRiskBased
        ? {
            isDefaultPlan: true,
            planStatus: createPricingPlan?.active,
            pricingPlanType: createPricingPlan?.variable
          }
        : {
            isDefaultPlan: false,
            planStatus: createPricingPlan?.active,
            pricingPlanType: createPricingPlan?.standard
          };
      const dataToSend = {
        ...pricingPlan,
        ...additionalValues,
        primaryPricingPlanId: pricingPlan.primaryPricingPlanId
          ? Number(pricingPlan.primaryPricingPlanId)
          : null,
        invoiceSyncType:
          pricingPlan.invoiceSyncType.trim() !== ""
            ? pricingPlan.invoiceSyncType
            : null,
        eligibleOrganizationType:
          pricingPlan.eligibleOrganizationType.trim() !== ""
            ? pricingPlan.eligibleOrganizationType
            : null,
        applicableCountry:
          pricingPlan.applicableCountry.trim() !== ""
            ? pricingPlan.applicableCountry
            : null,
        ownershipStatus:
          pricingPlan.ownershipStatus.trim() !== ""
            ? pricingPlan.ownershipStatus
            : null,
        triggerEvents: [pricingPlan.triggerEvents.join(",")],
        isAutoSwitching: pricingPlan.isAutoSwitching
      };
      const response = await privateAxios.post(
        getCreateCompletePricingPlanURL,
        dataToSend
      );
      showToast(response.data.message, showToastType.success);
      history(PRICING_PLAN_WITH_PLAN_TYPE);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg?.item4;

      showToast(errorMessage, showToastType.error);
    }
  };

  const handleCancel = () => {
    setPricingPlan({
      pricingPlanName: "",
      flatRate: "",
      annualRate: "",
      extraFlatRate: "",
      extraAnnualRate: "",
      isAutoSwitching: true,
      triggerEvents: [],
      industryCategoryId: "",
      invoiceSyncType: "",
      eligibleOrganizationType: "",
      applicableCountry: "",
      ownershipStatus: "",
      isRiskBased: false,
      primaryPricingPlanId: id,
      minApplicableValue: "",
      maxApplicableValue: ""
    });
    setErrors({});
    history(PRICING_PLAN_WITH_PLAN_TYPE);
  };

  const handleSelectChange = (selectedOptions) => {
    setPricingPlan({
      ...pricingPlan,
      triggerEvents: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : []
    });
    setErrors({ ...errors, triggerEvents: "" });
  };
  const selectedTriggers = useMemo(
    () =>
      userStatus.filter((opt) => pricingPlan.triggerEvents.includes(opt.value)),
    [userStatus, pricingPlan.triggerEvents]
  );
  return (
    <>
      <TablePagesTitle
        mainTitle={createPricingPlanGrp.mainTitle}
        content={createPricingPlanGrp.content}
      />

      <AccessPageAction subheading={createPricingPlanGrp.content} />

      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form">
          <div className="row mb-2">
            <div className="col-sm-4">
              <div className="input-title">
                <label htmlFor="pricing">
                  {createPricingPlan?.pricingName}
                </label>
              </div>
            </div>
            <div className="col-sm-8">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Pricing plan name"
                  name="pricingPlanName"
                  value={pricingPlanName}
                  onChange={onInputChange}
                />
                {errors.pricingPlanName && (
                  <div className="text-danger small">
                    {errors.pricingPlanName}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row ">
            <div className="col-sm-12 mb-2">
              <div className="input-title">
                <label htmlFor="standard">
                  {createPricingPlan?.standardPricing}
                </label>
              </div>
            </div>
            <div className="row col-sm-12 mb-2 mx-0 px-0">
              <div className="col-sm-4 ps-4">
                <div className="input-title">
                  <label htmlFor="flat">{createPricingPlan?.flatRate}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="number"
                    placeholder={createPricingPlan?.placeHolder?.item1}
                    step={commonText?.step1}
                    min={ZERO}
                    name="flatRate"
                    onChange={onInputChange}
                    value={flatRate}
                  />
                  {errors.flatRate && (
                    <div className="text-danger small">{errors.flatRate}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row col-sm-12 mb-2 mx-0 px-0">
              <div className="col-sm-4 ps-4">
                <div className="input-title">
                  <label htmlFor="Pa">{createPricingPlan?.parate}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="number"
                    placeholder={createPricingPlan?.placeHolder?.item2}
                    min={ZERO}
                    step={commonText?.step1}
                    name="annualRate"
                    onChange={onInputChange}
                    value={annualRate}
                  />
                  {errors.annualRate && (
                    <div className="text-danger small">{errors.annualRate}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row ">
            <div className="col-sm-12 mb-2">
              <div className="input-title">
                <label htmlFor="overdue">{createPricingPlan?.overdue}</label>
              </div>
            </div>
            <div className="row col-sm-12 mb-2 mx-0 px-0">
              <div className="col-sm-4 ps-4">
                <div className="input-title">
                  <label htmlFor="flat">
                    {createPricingPlan?.flatRateAddnl}
                  </label>
                </div>
              </div>
              <div className="col-sm-8 ">
                <div className="input-group">
                  <input
                    type="number"
                    placeholder={createPricingPlan?.placeHolder?.item1}
                    min={ZERO}
                    step={commonText?.step1}
                    name="extraFlatRate"
                    onChange={onInputChange}
                    value={extraFlatRate}
                  />
                  {errors.extraFlatRate && (
                    <div className="text-danger small">
                      {errors.extraFlatRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row col-sm-12 mb-2 mx-0 px-0">
              <div className="col-sm-4 ps-4">
                <div className="input-title">
                  <label htmlFor="pa">{createPricingPlan?.parateAddnl}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="number"
                    placeholder={createPricingPlan?.placeHolder?.item2}
                    min={ZERO}
                    step={commonText?.step1}
                    name="extraAnnualRate"
                    onChange={onInputChange}
                    value={extraAnnualRate}
                  />
                  {errors.extraAnnualRate && (
                    <div className="text-danger small">
                      {errors.extraAnnualRate}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-sm-4">
              <div className="input-title">
                <label htmlFor="autoSwithing">
                  {createPricingPlan?.autoSwithing}
                </label>
              </div>
            </div>
            <div className="col-sm-8">
              <div className="row input-group element_gap">
                <div className="col-md-12">
                  <input
                    type="radio"
                    name="isAutoSwitching"
                    id="active"
                    value={createAccPage?.active}
                    checked={pricingPlan.isAutoSwitching === true}
                    onChange={onInputChange}
                  />
                  <label htmlFor="active">{createAccPage?.active}</label>
                </div>
                <div className="col-md-12 ">
                  <input
                    type="radio"
                    name="isAutoSwitching"
                    id="inactive"
                    value={createAccPage?.inactive}
                    checked={pricingPlan.isAutoSwitching === false}
                    onChange={onInputChange}
                  />
                  <label htmlFor="inactive">{createAccPage?.inactive}</label>
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-sm-4">
              <div className="input-title">
                <label htmlFor="trigger">
                  {createPricingPlan?.triggerForoverdue}
                </label>
              </div>
            </div>
            <div className="col-sm-8">
              <div className="input-group">
                <Select
                  isMulti
                  name="triggerEvents"
                  options={userStatus}
                  onChange={handleSelectChange}
                  value={selectedTriggers}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
                {errors.triggerEvents && (
                  <div className="text-danger small">
                    {errors.triggerEvents}
                  </div>
                )}
              </div>
            </div>
          </div>

          {isRiskBased === false ? (
            <>
              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="forIndustryType">
                      {createPricingPlan?.forIndustryType}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <IndustryTypeSelect
                      name="industryCategoryId"
                      id="industryCategoryId"
                      value={industryCategoryId}
                      onChange={onInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="accounting">
                      {myAccount?.tabNames.accSoftware}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <select
                      name="invoiceSyncType"
                      id="invoiceSyncType"
                      onChange={onInputChange}
                      value={invoiceSyncType || ""}
                    >
                      {getAccountingSoftwareOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="orgtype">
                      {createPricingPlan?.orgtype}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <select
                      name="eligibleOrganizationType"
                      id="eligibleOrganizationType"
                      onChange={onInputChange}
                      value={eligibleOrganizationType}
                    >
                      {getOrgTypeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="country">
                      {createPricingPlan?.country}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <select
                      name="applicableCountry"
                      id="applicableCountry"
                      onChange={onInputChange}
                      value={applicableCountry}
                    >
                      {getCountryOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="property">
                      {createPricingPlan?.propertyOwnership}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <select
                      name="ownershipStatus"
                      id="ownershipStatus"
                      onChange={onInputChange}
                      value={ownershipStatus || ""}
                    >
                      {getOwnPropertyOptions()?.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          <div className="row mb-2">
            <div className="col-sm-4">
              <div className="input-title">
                <label htmlFor="riskbased">
                  {createPricingPlan?.riskbased}
                </label>
              </div>
            </div>
            <div className="col-sm-8">
              <div className="input-group">
                <select
                  name="isRiskBased"
                  id="isRiskBased"
                  onChange={onInputChange}
                  value={
                    pricingPlan.isRiskBased
                      ? createPricingPlan?.propertyOptions?.item1
                      : createPricingPlan?.propertyOptions?.item2
                  }
                >
                  {getSingleUseOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {isRiskBased === true ? (
            <>
              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="select">
                      {createPricingPlan?.selectGrp}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <select
                      name="primaryPricingPlanId"
                      id="primaryPricingPlanId"
                      onChange={onInputChange}
                      value={primaryPricingPlanId}
                    >
                      <option value="">
                        {createPricingPlan?.selectGrpOptn}
                      </option>
                      {planGroup &&
                        planGroup.map((data) => (
                          <option value={data.id} key={data.id}>
                            {data.pricingPlanName}
                          </option>
                        ))}
                    </select>
                    {errors.primaryPricingPlanId && (
                      <div className="text-danger small">
                        {errors.primaryPricingPlanId}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="minlimit">
                      {createPricingPlan?.minlimit}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="number"
                      min={ZERO}
                      step={commonText?.step1}
                      name="minApplicableValue"
                      placeholder={createPricingPlan?.placeHolder?.item2}
                      onChange={onInputChange}
                      value={minApplicableValue}
                    />
                    {errors.minApplicableValue && (
                      <div className="text-danger small">
                        {errors.minApplicableValue}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="maxlimit">
                      {createPricingPlan?.maxlimit}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="number"
                      min={ZERO}
                      step={commonText?.step1}
                      name="maxApplicableValue"
                      placeholder={createPricingPlan?.placeHolder?.item2}
                      onChange={onInputChange}
                      value={maxApplicableValue}
                    />
                    {errors.maxApplicableValue && (
                      <div className="text-danger small">
                        {errors.maxApplicableValue}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <div className="row mt-4">
            <div className="col-sm-8 offset-sm-4 d-flex d-sm-block justify-content-center justify-content-sm-start">
              <button
                type="button"
                className="btn btn-red me-2"
                onClick={handleCancel}
              >
                {alertActions?.Cancel}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {buttonName?.create}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
